/**
 * 抽壳命令适配器。
 */

import { CadCommandType } from '../../../command/CadCommandType';
import { createFeatureReferenceBinding } from '../../../utils/feature/createFeatureReferenceBinding';
import type { FeatureReferenceBinding } from '../../binding/FeatureReferenceBinding';
import { buildShellModelTopologyBindings } from '../../binding/buildShellModelTopologyBindings';
import { ShellFeatureDefinitionModel } from '../../definition/shell/ShellFeatureDefinition';
import { ShellFeaturePrototype } from '../../prototype/shell/ShellFeaturePrototype';
import { FeatureCommandAdapter, type FeatureCommandBuildContext } from '../FeatureCommandAdapter';

/**
 * 抽壳命令适配器。
 */
export class ShellCommandAdapter extends FeatureCommandAdapter<ShellFeaturePrototype> {
    /**
     * @inheritdoc
     */
    public supports(command: CadCommand): boolean {
        return command.type === CadCommandType.SHELL;
    }

    /**
     * @inheritdoc
     */
    public createPrototypeFromCommand(command: CadCommand): ShellFeaturePrototype | null {
        if (!this.supports(command)) {
            return null;
        }

        const params = structuredClone(command.params as ShellParams);
        const definition = ShellFeatureDefinitionModel.fromParams({
            commandParams: params,
        });
        const bindings = this.buildBindingsFromParams(params);
        return new ShellFeaturePrototype({
            featureId: command.id,
            definition,
            bindings,
            sourceSketchId: null,
        });
    }

    /**
     * @inheritdoc
     */
    public createCommandFromPrototype(
        context: FeatureCommandBuildContext<ShellFeaturePrototype>,
    ): CadCommand {
        const commandParams = context.prototype.getDefinition().getCommandParams();
        return {
            id: context.prototype.getFeatureId(),
            type: CadCommandType.SHELL,
            clientId: context.clientId,
            timestamp: context.timestamp ?? Date.now(),
            params: {
                ...commandParams,
                featureKind: 'shell',
                referenceBindings: context.prototype.getBindings().map((binding) => binding.serialize()),
            },
        };
    }

    /**
     * 从命令参数恢复输入绑定。
     *
     * @param params 命令参数。
     * @returns 输入绑定集合。
     */
    private buildBindingsFromParams(params: ShellParams): FeatureReferenceBinding[] {
        const serializedBindings = params.referenceBindings ?? buildShellModelTopologyBindings({
            upstreamBodyObjectId: params.upstreamBodyObjectId,
            definition: params.definition,
        });
        return serializedBindings.map((binding) => createFeatureReferenceBinding(binding));
    }
}
